import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Одно значение представления — единственное место, где решается, как выглядит
// ячейка. Рендерер не знает ни одной сущности поимённо: тип поля приходит в
// ответе, и новая сущность в каталоге отрисовывается сама.
import type { ReactElement } from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';

import { formatDateTime, formatMoney } from '../../../shared/format/index.js';
import { isCellRef, type ViewCell, type ViewColumn } from '../../../views/result.js';
import {
  localizedEntityLabel,
  localizedEnumLabel,
  localizedInheritedVia,
} from '../../../views/localization.js';

/** Подсказка у приглушённой привязки: через что она пришла и почему приглушена. */
function inheritedHint(via: string): string {
  return i18n.t('crm.features.dynamic.features.views.components.viewcellvalue.80', {
    p0: localizedInheritedVia(via, i18n.locale),
  });
}

/** «Сделка», «Контакт» — вид записи рядом с названием: иначе непонятно, через что связь. */
function kindLabel(entity: string | null): string | null {
  return entity === null ? null : localizedEntityLabel(entity, i18n.locale);
}

const { Text } = Typography;

/** Значение перечисления показываем подписью из каталога, а не сырым кодом. */
export function ViewCellValue({
  entity,
  column,
  value,
}: {
  entity: string;
  column: ViewColumn;
  value: ViewCell;
}): ReactElement {
  useI18nSubscription();
  if (value === null || value === undefined || value === '') {
    return <Text type="secondary">—</Text>;
  }

  if (isCellRef(value)) {
    // Унаследованная привязка (компания задачи — от её сделки) приглушена и с
    // подсказкой: тот же приём, что в гриде сделок. Иначе она читается как
    // проставленная у самой записи.
    const label =
      value.href === null ? <span>{value.label}</span> : <Link to={value.href}>{value.label}</Link>;
    // Старые сообщения чата несут прежний флаг `inherited: true` вместо пути:
    // представления сохраняются вместе с ходом. Тогда путь был ровно один.
    const via = value.inheritedVia ?? (value.inherited === true ? 'contact' : undefined);
    if (via === undefined) {
      return label;
    }
    return (
      <Tooltip title={inheritedHint(via)}>
        <span className="opacity-70 [&_a]:text-stone-500">{label}</span>
      </Tooltip>
    );
  }

  if (Array.isArray(value)) {
    // Привязки записи: у заметки их может быть несколько сразу, и по ним видно,
    // почему запись вообще попала в выдачу. Метки — просто строки.
    return (
      <span className="inline-flex flex-wrap items-center gap-1">
        {value.map((item) =>
          typeof item === 'string' ? (
            <Tag key={item} style={{ margin: 0 }}>
              {item}
            </Tag>
          ) : (
            <Tag key={`${item.entity ?? 'ref'}:${item.id}`} style={{ margin: 0 }}>
              {kindLabel(item.entity) === null ? null : (
                <span className="text-stone-500">{kindLabel(item.entity)}: </span>
              )}
              {item.href === null ? item.label : <Link to={item.href}>{item.label}</Link>}
            </Tag>
          ),
        )}
      </span>
    );
  }

  switch (column.type) {
    case 'money':
      return <span>{formatMoney(Number(value))}</span>;
    case 'date':
      return <span>{formatDateTime(String(value))}</span>;
    case 'enum':
      return <Tag>{localizedEnumLabel(entity, column, String(value), i18n.locale)}</Tag>;
    case 'boolean':
      return (
        <span>
          {value === true
            ? i18n.t('crm.features.features.views.components.viewcellvalue.да')
            : i18n.t('crm.features.features.views.components.viewcellvalue.нет')}
        </span>
      );
    case 'text':
      // Длинный текст в ячейке обрезаем: договор целиком ломает грид.
      return (
        <Text ellipsis={{ tooltip: String(value) }} style={{ maxWidth: 320 }}>
          {String(value)}
        </Text>
      );
    default:
      return <span>{String(value)}</span>;
  }
}
